import { OperationVariables, useApolloClient } from "@apollo/client";
import { ApolloQueryResult } from "@apollo/client/core/types";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { useWatchDependency } from "@sinch/hooks/utilities/useWatchDependency";
import { MarginCategory, RateComposition, RatelistOverride } from "@sinch/types/sinch.types";
import type { DocumentNode } from "graphql/index";
import { length, pluck, uniq } from "ramda";
import { isArray, isNilOrEmpty, isNotNil } from "ramda-adjunct";
import { useCallback, useMemo, useRef, useState } from "react";
import { TableItemsSource } from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/rowBuilders";
import {
  JobTree,
  OrderTree,
  PositionTree,
  ShiftTree,
} from "@sinch/components/application/RatesCompositionTable/queries.graphql";
import {
  JobTreeQuery,
  JobTreeQueryVariables,
  OrderTreeQuery,
  OrderTreeQueryVariables,
  PositionTreeQuery,
  PositionTreeQueryVariables,
  ShiftTreeQuery,
  ShiftTreeQueryVariables,
} from "@sinch/components/application/RatesCompositionTable/queries.types";
import {
  RatelistScope,
  RatesCompositionTableProps,
} from "@sinch/components/application/RatesCompositionTable/RatesCompositionTable";

export const TOTAL_ROW_ID = "__total__";

type GroupedQueryResult<TResult> = { label: string | null; data: TResult | null };

const useMultiLazyLoad = <
  TData = Record<string, unknown>,
  TVariables extends OperationVariables = OperationVariables,
  TResult = unknown,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  queries: { label: string | null; variables: TVariables }[],
  dataGetter: (data: ApolloQueryResult<TData>) => TResult
): { results: GroupedQueryResult<TResult>[]; loading: boolean } => {
  const { query: apolloQuery } = useApolloClient();
  const [results, setResults] = useState<GroupedQueryResult<TResult>[]>([{ label: null, data: null }]);
  const [loading, setLoading] = useState<boolean>(false);
  // Identifies the newest dispatched batch. The queries are refired whenever their variables change,
  // which for an unsaved ratelist override happens while the user types, so a slow earlier batch can
  // still resolve after a newer one and must not be allowed to overwrite it.
  const latestRequestId = useRef(0);

  useWatchDependency(() => {
    const requestId = ++latestRequestId.current;

    if (isNilOrEmpty(queries)) {
      setResults([]);
    }
    setLoading(true);
    Promise.all(
      queries.map(({ label, variables }) =>
        apolloQuery<TData, TVariables>({ query, variables, fetchPolicy: "network-only" }).then((result) => ({
          label,
          data: dataGetter(result),
        }))
      )
    )
      .then((response) => {
        if (requestId !== latestRequestId.current) {
          return;
        }
        setResults(response);
      })
      // A rejected composition query must not leave the grid stuck in its loading state, the previously
      // loaded results are kept instead. A superseded batch leaves the state to the newer one, which is
      // still in flight and owns the loading flag.
      .finally(() => {
        if (requestId !== latestRequestId.current) {
          return;
        }
        setLoading(false);
      });
  }, [queries]);

  return { results, loading };
};

/**
 * Builds the ratelist override argument of a composition query for one side of the rates.
 * Scopes without any ratelist are left out and exactly one of `ratelist` / `ratelistId` is sent,
 * the server rejects overrides carrying both or neither.
 *
 * @param ratelistScopes - Scopes the table is rendered for
 * @param rateCategory - Whether the price or the wage ratelists of the scopes are wanted
 */
const buildRatelistOverrides = (
  ratelistScopes: RatelistScope[],
  rateCategory: "price" | "wage"
): RatelistOverride[] =>
  ratelistScopes.flatMap<RatelistOverride>(
    ({ category, priceRatelist, priceRatelistId, wageRatelist, wageRatelistId }) => {
      const ratelist = rateCategory === "price" ? priceRatelist : wageRatelist;
      const ratelistId = rateCategory === "price" ? priceRatelistId : wageRatelistId;

      if (isNotNil(ratelist)) {
        return [{ category, ratelist }];
      }

      return isNotNil(ratelistId) ? [{ category, ratelistId }] : [];
    }
  );

export type ExtendedRateComposition = {
  total: RateComposition["total"];
  components: (RateComposition["components"][0] & { ratelistId?: string })[];
};
/**
 * @param copyPreviewDayShift - Evaluate the composition as the copy dialog preview, shifted by this many days.
 *   Sent as the `copyPreviewDayShift` variable of every tree query.
 */
export const useTreeQueries = (
  ids: RatesCompositionTableProps["ids"],
  ratelistScopes: RatelistScope[],
  groupBy?: { label: string; groups: MarginCategory[] }[],
  copyPreviewDayShift?: number | null
) => {
  const priceRatelistOverride = useMemo(() => buildRatelistOverrides(ratelistScopes, "price"), [ratelistScopes]);
  const wageRatelistOverride = useMemo(() => buildRatelistOverrides(ratelistScopes, "wage"), [ratelistScopes]);
  const categories = useMemo(() => uniq(pluck("category", ratelistScopes)), [ratelistScopes]);

  const buildQueries = useCallback(
    (entityIds: string[]) => {
      if (isNilOrEmpty(entityIds) || (isArray(categories) && length(categories) === 0)) {
        return [];
      }
      return (
        groupBy?.map(({ label, groups }) => ({
          label,
          variables: {
            ids: entityIds,
            priceRatelistOverride,
            wageRatelistOverride,
            categories: categories.filter((category) => groups.includes(category)),
            copyPreviewDayShift: copyPreviewDayShift ?? null,
          },
        })) ?? [
          {
            label: null,
            variables: {
              ids: entityIds,
              priceRatelistOverride,
              wageRatelistOverride,
              categories,
              copyPreviewDayShift: copyPreviewDayShift ?? null,
            },
          },
        ]
      );
    },
    [categories, groupBy, priceRatelistOverride, wageRatelistOverride, copyPreviewDayShift]
  );

  const orderQueries = useMemo(() => buildQueries(ids.orderId!), [buildQueries, ids.orderId]);
  const { results: orderTreeQuery, loading: orderLoading } = useMultiLazyLoad<
    OrderTreeQuery,
    OrderTreeQueryVariables,
    TableItemsSource<ExtendedRateComposition>[]
  >(OrderTree, orderQueries, ({ data }) => data?.orders ?? []);

  const jobQueries = useMemo(() => buildQueries(ids.jobId!), [buildQueries, ids.jobId]);
  const { results: jobTreeQuery, loading: jobLoading } = useMultiLazyLoad<
    JobTreeQuery,
    JobTreeQueryVariables,
    TableItemsSource<ExtendedRateComposition>[]
  >(JobTree, jobQueries, ({ data }) => data?.jobs ?? []);

  const shiftQueries = useMemo(() => buildQueries(ids.shiftId!), [buildQueries, ids.shiftId]);
  const { results: shiftTreeQuery, loading: shiftLoading } = useMultiLazyLoad<
    ShiftTreeQuery,
    ShiftTreeQueryVariables,
    TableItemsSource<ExtendedRateComposition>[]
  >(ShiftTree, shiftQueries, ({ data }) => data?.shifts ?? []);

  const positionQueries = useMemo(() => buildQueries(ids.positionId!), [buildQueries, ids.positionId]);
  const { results: positionTreeQuery, loading: positionLoading } = useMultiLazyLoad<
    PositionTreeQuery,
    PositionTreeQueryVariables,
    TableItemsSource<ExtendedRateComposition>[]
  >(PositionTree, positionQueries, ({ data }) => data?.positions ?? []);

  const loading = orderLoading || jobLoading || shiftLoading || positionLoading;

  return { orderTreeQuery, jobTreeQuery, shiftTreeQuery, positionTreeQuery, loading };
};
